import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { AuthService } from "@app/services/AuthService";
import { AuthUtil } from "@app/utils/AuthUtil";
import { TokenRefreshLock } from "@app/utils/TokenRefreshLock";

export class AxiosUtil {

    public static baseURL: string = '';
    public static locale: string = 'en';

    static createPreconfiguredAxiosInstance(): AxiosInstance {
        const newInstance = axios.create({ timeout: 60000 });
        this.registerGlobalInterceptors(newInstance);

        return newInstance;
    }

    static createNonConfiguredAxiosInstance(): AxiosInstance {
        const newInstance = axios.create({ timeout: 60000 });

        newInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
            if (config.headers) {
                config.headers['x-custom-lang'] = this.locale;
            }
            return config;
        }, (error) => {
            // Do something with request error
            return Promise.reject(error);
        });

        return newInstance;
    }

    static configureAxios(baseURL: string, locale: string) {
        this.baseURL = baseURL;
        this.locale = locale;

        axios.defaults.baseURL = this.baseURL;
    }

    /**
     * For implementing global interceptors
     * @private
     */
    private static registerGlobalInterceptors(instance: AxiosInstance) {
        // request interceptors
        instance.interceptors.request.use(async (config) => {
            const accessToken = AuthUtil.getAccessToken();

            if (config.headers) {
                if (accessToken) {
                    // Add the access token to the 'Authorization' header
                    config.headers['Authorization'] = `Bearer ${accessToken}`;
                }

                config.headers["x-custom-lang"] = this.locale;
                config.headers['x-device'] = AuthUtil.getDeviceId();
            }
            return Promise.resolve(config);
        }, (error) => {
            // Do something with request error
            return Promise.reject(error);
        });

        // error validation on response
        instance.interceptors.response.use(
            (response: AxiosResponse) => {
                return interceptorOnResponseSuccessHandler(response, instance);
            },
            async (error: AxiosError) => {
                return await interceptorOnResponseErrorHandler(error, instance);
            }
        )
    }
}

const interceptorOnResponseSuccessHandler = (response: AxiosResponse, instance: AxiosInstance) => {
    return response;
}

// Cross-tab coordination for token rotation.
// The server invalidates the refresh token on every rotation, so concurrent
// rotations from multiple tabs (or multiple 401s in one tab) would trip over
// each other: the loser sends an already-rotated token, gets a 401 and logs
// the user out everywhere (tokens live in shared localStorage).
// TokenRefreshLock single-flights rotations across tabs (Web Locks API with
// an in-tab fallback); lastSuccessfulRefresh lets a waiter reuse the winner's
// result without issuing another rotation call.
let lastSuccessfulRefresh: { accessToken: string; basedOnRefreshToken: string } | null = null;

export const resetTokenRefreshStateForTesting = () => {
    lastSuccessfulRefresh = null;
    TokenRefreshLock.resetFallbackQueueForTesting();
};

const interceptorOnResponseErrorHandler = async (error: AxiosError, instance: AxiosInstance) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest.headers.get('IsRetry') !== 'true') {
        // If the request returns 401 Unauthorized and it hasn't been retried already
        originalRequest.headers.set('IsRetry', 'true');

        // Capture the refresh token that was valid when this request failed
        const refreshTokenAtFailure = AuthUtil.getRefreshToken();

        if (AuthUtil.getRefreshTokenExpired()) {
            await AuthService.logout();
            return Promise.reject('Refresh token expired');
        }

        if (refreshTokenAtFailure) {
            try {
                // Single-flight the rotation across all tabs of this origin.
                // Tokens are re-read INSIDE the lock: a caller that waited for
                // another tab's rotation picks up the rotated tokens instead of
                // sending its stale (already invalidated) refresh token.
                const newAccessToken = await TokenRefreshLock.withLock(async () => {
                    const currentRefreshToken = AuthUtil.getRefreshToken();
                    const tokenWasRotatedElsewhere = currentRefreshToken !== refreshTokenAtFailure;

                    if (tokenWasRotatedElsewhere) {
                        // Another tab already rotated while we waited for the lock
                        const freshAccessToken = AuthUtil.getAccessToken();
                        if (freshAccessToken && !AuthUtil.getAccessTokenExpired()) {
                            return freshAccessToken;
                        }

                        if (!currentRefreshToken) {
                            // A logout in another tab cleared the shared session
                            return Promise.reject('Session ended in another tab');
                        }

                        if (AuthUtil.getRefreshTokenExpired()) {
                            await AuthService.logout();
                            return Promise.reject('Refresh token expired');
                        }
                    } else if (lastSuccessfulRefresh
                        && lastSuccessfulRefresh.basedOnRefreshToken === refreshTokenAtFailure) {
                        // This tab already rotated exactly this token while the
                        // request waited for the lock; reuse its access token
                        return lastSuccessfulRefresh.accessToken;
                    }

                    const tokenToExchange = currentRefreshToken ?? refreshTokenAtFailure;

                    return AuthService.requestNewAccessToken(tokenToExchange)
                        .then((accessToken: string | null) => {
                            if (accessToken) {
                                lastSuccessfulRefresh = {
                                    accessToken,
                                    basedOnRefreshToken: tokenToExchange,
                                };
                            }
                            return accessToken;
                        })
                        .catch((refreshError: any) => {
                            const statusCode = refreshError?.statusCode || refreshError?.status;

                            // Only logout if the server explicitly rejected this
                            // exact token (401/403). For network errors or
                            // timeouts the session may still be valid.
                            if (statusCode === 401 || statusCode === 403) {
                                // Re-read before logout: if storage now holds a
                                // different refresh token, our 401 came from a
                                // stale token in a rotation race and the session
                                // in storage is still valid - never revoke it.
                                if (AuthUtil.getRefreshToken() === tokenToExchange) {
                                    return AuthService.logout()
                                        .then(() => Promise.reject(refreshError));
                                }

                                const freshAccessToken = AuthUtil.getAccessToken();
                                if (freshAccessToken && !AuthUtil.getAccessTokenExpired()) {
                                    return freshAccessToken;
                                }
                            }

                            return Promise.reject(refreshError);
                        });
                });

                if (!newAccessToken) {
                    return Promise.reject(error);
                }

                // Update the 'Authorization' header with the new access token
                instance.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;

                // Retry the original request with the new access token
                return instance(originalRequest);
            } catch (refreshError) {
                return Promise.reject(refreshError);
            }
        }
    }

    return Promise.reject(error);
}
